<script setup lang="ts">
import { ref } from "vue";
import Button from "@/components/Reusables/Buttons/Button.vue";
import DropDown from "@/components/Reusables/Inputs/DropDown.vue";
import { useToast } from "vue-toastification";
import { uploadIFCModel } from "@/api/services/Speckle";
import { BuildingProjectType } from "@/features/BuildingProject";

import { useModalStore } from "@/stores/modals/modals.store";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";

const modals = useModalStore();

// The toolbar's Upload trigger is already inert for a Viewer and while previewing; this is the
// second layer, for a modal left open across a role or revision change (RTLCA-5253, RTLCA-5582).
const { blockedByPreview } = useProjectEditReadOnly();

const props = defineProps<{
  project: BuildingProjectType;
  currentBranchName: string;
  allBranchNames: Array<string>;
}>();

const isHoveringFile = ref<boolean>(false);
const currentBranchName = ref<string>(props.currentBranchName);
const fileWasAccepted = ref<boolean>(false);
const newFileWasUploaded = ref<number>(0);
const isImporting = ref<boolean>(false);
const hasError = ref<boolean>(false);
const showProcessing = ref<boolean>(false);

const uploadResponse = ref<boolean>();

const file = ref<HTMLInputElement>();

const toggleIsHoveringFile = (state: boolean) => {
  isHoveringFile.value = state;
};

const handleFileChange = () => {
  if (!file.value || !file.value.files) return;

  uploadResponse.value = undefined;
  const isValidExtension = file.value.files[0].name.toLowerCase().endsWith("ifc");

  if (!isValidExtension) {
    useToast().error("Please use .ifc files");
    file.value.type = "text";
    file.value.type = "file";

    return;
  }

  fileWasAccepted.value = true;
  newFileWasUploaded.value++;
};

const handleImportModel = async () => {
  // The upload menu is already closed while a frozen revision is previewed; this covers a modal that
  // was already open when the revision changed — the import would land on the current revision.
  if (blockedByPreview()) return;
  if (!file.value || !file.value.files || !fileWasAccepted.value) return;

  isImporting.value = true;
  hasError.value = false;

  // typescript ceremonies -- this should never happen
  if (!props.project.speckleData.streamId || !currentBranchName.value) {
    console.error("missing stream Id or branch name");
    isImporting.value = true;
    return;
  }
  uploadResponse.value = await uploadIFCModel({
    streamId: props.project.speckleData.streamId,
    modelName: currentBranchName.value,
    file: file.value.files[0],
  }).catch((_err) => {
    useToast().error("Something went wrong with your upload");
    hasError.value = true;
    isImporting.value = false;
    return false;
  });

  if (uploadResponse.value) {
    isImporting.value = false;
    useToast().success("Imported IFC file successfully");
    showProcessing.value = true;

    modals.close(showProcessing.value);
  } else {
    hasError.value = true;
    useToast().error("Something went wrong with your upload");
  }

  isImporting.value = false;
};

const removeFile = () => {
  if (!file.value || !file.value.files) return;
  file.value.type = "text";
  file.value.type = "file";
  uploadResponse.value = undefined;
  fileWasAccepted.value = false;
};
</script>

<template>
  <div class="text-text-color relative flex w-fit flex-col gap-8">
    <div>
      <p class="t-h4 text-type-default">Upload IFC file</p>
      <div class="flex items-end justify-between">
        <DropDown
          :key="currentBranchName"
          v-model:value="currentBranchName"
          color="primary"
          label="Upload to model"
          title="Select model"
          :options="allBranchNames"
          data-cy="branch-select" />
      </div>
    </div>

    <div
      class="rounded-16 relative flex h-44 w-full cursor-pointer flex-col items-center justify-center border-4 border-dashed"
      :class="[isHoveringFile ? 'border-app-blue-600' : hasError ? 'border-app-red-500' : 'border-input-stroke']"
      @dragover="toggleIsHoveringFile(true)"
      @dragleave="toggleIsHoveringFile(false)"
      @drop="toggleIsHoveringFile(false)">
      <input
        ref="file"
        type="file"
        accept=".ifc"
        class="absolute inset-0 h-full w-full opacity-0"
        @change="handleFileChange" />
      <font-awesome-icon
        v-if="!fileWasAccepted"
        icon="fa-solid fa-file-arrow-up" />
      <span v-if="!fileWasAccepted">
        <p class="underline">Click to upload</p>
        or drag and drop
      </span>

      <div
        v-if="fileWasAccepted && file && file.files"
        :key="newFileWasUploaded"
        class="border-element-stroke bg-surface rounded-16 relative flex h-32 w-32 flex-col items-center justify-between border shadow-xs">
        <div
          class="bg-button-default text-type-muted hover:text-app-red-500 absolute -top-1 -right-1 z-20 flex h-1 w-1 items-center justify-center rounded-full p-2 text-[9px] leading-none"
          @click="removeFile">
          <font-awesome-icon
            icon="fa-solid fa-x"
            class="h-2 w-2" />
        </div>
        <div></div>
        <font-awesome-icon
          icon="fa-solid fa-file"
          class="w-1/2" />

        <div class="bg-surface text-type-muted w-full p-2 text-center text-xs leading-none mix-blend-multiply">
          {{ file.files[0].name }}
        </div>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <a
        href="https://realtimelca.com/en-us/instructions/#upload-ifc-model"
        target="_blank"
        ><font-awesome-icon
          icon="fa-solid fa-circle-exclamation"
          class="cursor-pointer text-xl text-slate-500 transition-all hover:text-slate-500/50"
      /></a>
      <span class="text-sm">
        <b>Important:</b> Please watch
        <a
          class="text-app-blue-600 font-semibold underline hover:no-underline"
          href="https://realtimelca.com/en-us/instructions/#upload-ifc-model"
          target="_blank"
          >this tutorial</a
        >
        before uploading IFC files.
      </span>
    </div>
    <div class="flex w-full justify-between">
      <div class="flex gap-2">
        <Button
          color="button"
          label="Cancel"
          @click="modals.close()" />
      </div>
      <RevisionGatedButton
        color="primary"
        :is-loading="isImporting"
        :label="isImporting ? 'Uploading...' : 'Upload'"
        data-cy="import-ifc-btn"
        :guard="useProjectEditReadOnly"
        @click="handleImportModel" />
    </div>
  </div>
</template>
